/*
 * The folder pane: 240px on the surface. Where you are is the panel's pale brand tint with brand text; counts are
 * quiet numbers (unread in the foreground, totals faint). Conversations can be dragged onto any folder to move
 * them there. Counts come from IMAP STATUS.
 */
import { useState, type DragEvent, type ReactNode } from "react";
import { useBrandName } from "../lib/brand";
import { cx } from "../lib/cx";
import { IconMore, IconPlus } from "../ui/icons";
import { Menu, MenuContent, MenuItem, MenuTrigger } from "../ui/Menu";
import { STARRED, UNIFIED } from "./data";
import { IconAlarm, IconArchiveBox, IconDraft, IconFolder, IconInbox, IconInboxes, IconSearchSaved, IconSend, IconSendLater, IconSpam, IconStar, IconTrashCan } from "./icons";
import { SectionLabel } from "./parts";
import type { Folder, FolderRole, FoldersResult, SavedSearch } from "./types";

const ROLE_ICON: Record<FolderRole, typeof IconInbox> = { inbox: IconInbox, drafts: IconDraft, sent: IconSend, archive: IconArchiveBox, junk: IconSpam, trash: IconTrashCan, scheduled: IconSendLater, snoozed: IconAlarm };

/** The drag type a conversation carries while it's dragged (MessageList sets it). */
export const DRAG_TYPE = "application/x-hosterr-mail";

function Item({
  active,
  icon,
  label,
  unread,
  count,
  onClick,
  depth = 0,
  menu,
  onDrop,
}: {
  active: boolean;
  icon: ReactNode;
  label: string;
  unread: number;
  count: number;
  onClick: () => void;
  depth?: number;
  menu?: ReactNode;
  /** Conversations dropped here move here; absent where they can't (Starred, Drafts, Sent). */
  onDrop?: () => void;
}) {
  const [over, setOver] = useState(false);
  const accepts = (e: DragEvent) => !!onDrop && e.dataTransfer.types.includes(DRAG_TYPE);
  return (
    <div
      className="group relative"
      onDragOver={(e) => {
        if (!accepts(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (!accepts(e)) return;
        e.preventDefault();
        setOver(false);
        onDrop!();
      }}
    >
      <button
        type="button"
        onClick={onClick}
        aria-current={active ? "page" : undefined}
        className={cx(
          "inline-flex h-9 w-full items-center gap-2.5 rounded-[9px] px-2.5 text-[14px] transition-colors focus-visible:outline-2 focus-visible:outline-brand-500",
          over ? "bg-selected text-selected-fg shadow-[inset_0_0_0_1.5px_var(--color-brand-500)]" : active ? "bg-selected font-medium text-selected-fg" : "text-body hover:bg-hover hover:text-fg",
        )}
        style={depth ? { paddingLeft: `${10 + depth * 14}px` } : undefined}
      >
        <span className={cx("shrink-0", active || over ? "text-current" : "text-dim")}>{icon}</span>
        <span className="min-w-0 flex-1 truncate text-left">{label}</span>
        {unread > 0 ? (
          <span className={cx("text-[12px] font-semibold tabular-nums", active ? "text-current" : "text-fg", !!menu && "group-hover:invisible")}>{unread.toLocaleString("en-US")}</span>
        ) : count > 0 ? (
          <span className={cx("text-[12px] text-faint tabular-nums", !!menu && "group-hover:invisible")}>{count.toLocaleString("en-US")}</span>
        ) : null}
      </button>
      {menu ? <div className="absolute top-1/2 right-1 hidden -translate-y-1/2 group-focus-within:block group-hover:block">{menu}</div> : null}
    </div>
  );
}

export function FolderPane({
  data,
  current,
  address,
  openedBy,
  onGo,
  onNewFolder,
  onRename,
  onDelete,
  onEmpty,
  onDropTo,
  unified,
  saved,
  currentSearch,
  onSearch,
  onForgetSearch,
}: {
  data: FoldersResult | undefined;
  current: string | null;
  address: string;
  /** "Opened from Hosterr by Ryan Lovett": the mailbox was opened from the panel, not with its password. */
  openedBy: string | null | undefined;
  onGo: (path: string) => void;
  onNewFolder: () => void;
  onRename: (f: Folder) => void;
  onDelete: (f: Folder) => void;
  onEmpty: (f: Folder) => void;
  /** Conversations dragged onto a folder. */
  onDropTo: (f: Folder) => void;
  /** "All inboxes": shown when the session has more than one mailbox, with their unread count together. */
  unified: { unseen: number } | null;
  /** Saved searches (prefs), under the folders. */
  saved: SavedSearch[];
  currentSearch: string | null;
  onSearch: (q: string) => void;
  onForgetSearch: (s: SavedSearch) => void;
}) {
  const brand = useBrandName();
  const special = data?.folders.filter((f) => f.role) ?? [];
  const own = data?.folders.filter((f) => !f.role) ?? [];
  const inbox = special.find((f) => f.role === "inbox");
  const rest = special.filter((f) => f.role !== "inbox");
  const droppable = (f: Folder) => (f.role === "drafts" || f.role === "sent" || f.role === "scheduled" || f.role === "snoozed" ? undefined : () => onDropTo(f));
  const roleItem = (f: Folder) => {
    const Icon = ROLE_ICON[f.role!];
    const emptiable = f.role === "trash" || f.role === "junk";
    return (
      <Item
        key={f.path}
        active={current === f.path}
        icon={<Icon size={16} />}
        label={f.name}
        // Unread counts where unread means something; Spam, Trash and the waiting folders show only how many are there.
        unread={f.role === "junk" || f.role === "trash" || f.role === "sent" || f.role === "scheduled" || f.role === "snoozed" ? 0 : f.unseen}
        count={f.role === "drafts" || f.role === "junk" || f.role === "scheduled" || f.role === "snoozed" ? f.messages : 0}
        onClick={() => onGo(f.path)}
        onDrop={droppable(f)}
        menu={
          emptiable && f.messages > 0 ? (
            <Menu>
              <MenuTrigger aria-label={`${f.name} options`} className="inline-flex h-7 w-7 items-center justify-center rounded text-dim hover:bg-panel hover:text-fg">
                <IconMore size={15} />
              </MenuTrigger>
              <MenuContent align="start" className="min-w-[180px]">
                <MenuItem danger onSelect={() => onEmpty(f)}>
                  Empty {f.name}
                </MenuItem>
              </MenuContent>
            </Menu>
          ) : undefined
        }
      />
    );
  };

  return (
    <nav aria-label="Folders" className="flex-1 overflow-y-auto py-3">
      <div className="space-y-0.5 px-3">
        {unified ? <Item active={current === UNIFIED} icon={<IconInboxes size={16} />} label="All inboxes" unread={unified.unseen} count={0} onClick={() => onGo(UNIFIED)} /> : null}
        {inbox ? roleItem(inbox) : null}
        <Item active={current === STARRED} icon={<IconStar size={16} />} label="Starred" unread={0} count={0} onClick={() => onGo(STARRED)} />
        {rest.map(roleItem)}
        {!data ? (
          <div className="space-y-1.5 px-1 pt-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="hx-skeleton h-6 rounded" />
            ))}
          </div>
        ) : null}
      </div>

      <div className="mt-5 px-3">
        <div className="flex items-center justify-between pr-1">
          <SectionLabel className="pb-0">Your folders</SectionLabel>
          <button type="button" onClick={onNewFolder} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-dim hover:bg-hover hover:text-fg" aria-label="New folder">
            <IconPlus size={15} />
          </button>
        </div>
        <div className="mt-1 space-y-0.5">
          {own.map((f) => (
            <Item
              key={f.path}
              active={current === f.path}
              icon={<IconFolder size={16} />}
              label={f.name}
              depth={f.depth}
              unread={f.unseen}
              count={0}
              onClick={() => onGo(f.path)}
              onDrop={droppable(f)}
              menu={
                <Menu>
                  <MenuTrigger aria-label={`${f.name} options`} className="inline-flex h-7 w-7 items-center justify-center rounded text-dim hover:bg-panel hover:text-fg">
                    <IconMore size={15} />
                  </MenuTrigger>
                  <MenuContent align="start" className="min-w-[180px]">
                    <MenuItem onSelect={() => onRename(f)}>Rename</MenuItem>
                    <MenuItem danger onSelect={() => onDelete(f)}>
                      Delete folder
                    </MenuItem>
                  </MenuContent>
                </Menu>
              }
            />
          ))}
          {data && !own.length ? <p className="px-2.5 py-1 text-[12.5px] leading-snug text-dim">No folders yet.</p> : null}
        </div>
      </div>

      {saved.length ? (
        <div className="mt-5 px-3">
          <SectionLabel className="pb-0">Saved searches</SectionLabel>
          <div className="mt-1 space-y-0.5">
            {saved.map((x) => (
              <Item
                key={x.name}
                active={currentSearch === x.q}
                icon={<IconSearchSaved size={16} />}
                label={x.name}
                unread={0}
                count={0}
                onClick={() => onSearch(x.q)}
                menu={
                  <Menu>
                    <MenuTrigger aria-label={`${x.name} options`} className="inline-flex h-7 w-7 items-center justify-center rounded text-dim hover:bg-panel hover:text-fg">
                      <IconMore size={15} />
                    </MenuTrigger>
                    <MenuContent align="start" className="min-w-[200px]">
                      <MenuItem onSelect={() => onSearch(x.q)}>
                        <span className="font-mono text-[12px]">{x.q}</span>
                      </MenuItem>
                      <MenuItem danger onSelect={() => onForgetSearch(x)}>
                        Forget this search
                      </MenuItem>
                    </MenuContent>
                  </Menu>
                }
              />
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-6 px-5 text-[12px] leading-snug text-dim">
        <p className="truncate text-[12px]" title={address}>
          {address}
        </p>
        {openedBy !== undefined && openedBy !== null ? <p className="mt-1">Opened from {brand}{openedBy ? ` by ${openedBy}` : ""}</p> : null}
      </div>
    </nav>
  );
}
